import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, Filter, Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Task } from 'public-contracts';
import taskService from '../services/taskService';
import { format, addDays, startOfDay, differenceInDays, eachDayOfInterval, endOfMonth, startOfMonth, isSameDay, isWeekend } from 'date-fns';

const COLUMN_WIDTH = 40;
const ROW_HEIGHT = 48;
const TASK_LIST_WIDTH = 250;

export const ModernGanttChart: React.FC = () => {
  const { translate } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [searchTerm, setSearchTerm] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await taskService.getTasks();
      setTasks(data);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateA - dateB;
    });
  }, [tasks, searchTerm]);

  const timelineDays = useMemo(() => {
    const end = addDays(startDate, 30);
    return eachDayOfInterval({ start: startDate, end });
  }, [startDate]);

  const getTaskPosition = (task: Task) => {
    if (!task.startDate || !task.dueDate) return null;
    
    const taskStart = startOfDay(new Date(task.startDate));
    const taskEnd = startOfDay(new Date(task.dueDate));
    
    const startOffset = differenceInDays(taskStart, startDate);
    const duration = differenceInDays(taskEnd, taskStart) + 1;
    
    return {
      left: startOffset * COLUMN_WIDTH,
      width: Math.max(duration * COLUMN_WIDTH, COLUMN_WIDTH),
    };
  };

  const renderDependencies = () => {
    const lines: React.ReactNode[] = [];
    
    filteredTasks.forEach((task, taskIndex) => {
      if (!task.dependencies) return;
      
      task.dependencies.forEach(depId => {
        const depIndex = filteredTasks.findIndex(t => t.id === depId);
        if (depIndex === -1) return;
        
        const posFrom = getTaskPosition(filteredTasks[depIndex]);
        const posTo = getTaskPosition(task);
        
        if (!posFrom || !posTo) return;
        
        const x1 = posFrom.left + posFrom.width;
        const y1 = depIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
        const x2 = posTo.left;
        const y2 = taskIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
        
        // Simple path: right -> down/up -> right
        const midX = x1 + (x2 - x1) / 2;
        const path = `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
        
        lines.push(
          <path
            key={`${depId}-${task.id}`}
            d={path}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-muted-foreground/40"
            markerEnd="url(#arrowhead)"
          />
        );
      });
    });
    
    return lines;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b bg-background flex shrink-0 items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {translate('pos.project_management.gantt_chart', 'Gantt Chart')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {translate(
              'pos.project_management.gantt_subtitle',
              'Visualize project timeline and dependencies'
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={translate('common.search', 'Search...')}
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center border rounded-md">
            <Button variant="ghost" size="icon" onClick={() => setStartDate(addDays(startDate, -7))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-3 py-1 text-sm font-medium border-x">
              {format(startDate, 'MMM yyyy')}
            </div>
            <Button variant="ghost" size="icon" onClick={() => setStartDate(addDays(startDate, 7))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            {translate('common.filter', 'Filter')}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative flex">
        {/* Left: Task List */}
        <div 
          className="border-r bg-background shrink-0 flex flex-col z-20 shadow-sm"
          style={{ width: TASK_LIST_WIDTH }}
        >
          <div className="h-12 border-b flex items-center px-4 font-semibold text-sm bg-slate-50/50">
            {translate('pos.project_management.fields.title', 'Task')}
          </div>
          <div className="flex-1 overflow-y-auto no-scrollbar">
            {filteredTasks.map(task => (
              <div 
                key={task.id} 
                className="h-[48px] border-b flex items-center px-4 text-sm truncate hover:bg-slate-50 transition-colors"
                title={task.title}
              >
                {task.title}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Timeline */}
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/30">
          {/* Timeline Header */}
          <div className="h-12 border-b bg-slate-50/50 flex shrink-0 z-10 sticky top-0">
            {timelineDays.map(day => (
              <div 
                key={day.toISOString()} 
                className={`shrink-0 border-r flex flex-col items-center justify-center text-[10px] ${
                  isWeekend(day) ? 'bg-slate-100/50' : ''
                }`}
                style={{ width: COLUMN_WIDTH }}
              >
                <span className="font-bold opacity-70 uppercase">{format(day, 'EEE')}</span>
                <span className={isSameDay(day, new Date()) ? 'bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center' : ''}>
                  {format(day, 'd')}
                </span>
              </div>
            ))}
          </div>

          {/* Timeline Body */}
          <div 
            ref={scrollContainerRef}
            className="flex-1 overflow-auto relative"
          >
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="relative min-h-full" style={{ width: timelineDays.length * COLUMN_WIDTH }}>
                {/* Background Grid */}
                <div className="absolute inset-0 pointer-events-none flex">
                  {timelineDays.map(day => (
                    <div 
                      key={day.toISOString()} 
                      className={`h-full border-r shrink-0 ${isWeekend(day) ? 'bg-slate-100/30' : ''}`}
                      style={{ width: COLUMN_WIDTH }}
                    />
                  ))}
                </div>

                {/* SVG for Dependency Lines */}
                <svg 
                  className="absolute inset-0 pointer-events-none z-0"
                  style={{ width: '100%', height: filteredTasks.length * ROW_HEIGHT }}
                >
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="currentColor" className="text-muted-foreground/40" />
                    </marker>
                  </defs>
                  {renderDependencies()}
                </svg>

                {/* Task Bars */}
                <div className="relative z-10">
                  {filteredTasks.map((task, index) => {
                    const pos = getTaskPosition(task);
                    if (!pos) return <div key={task.id} className="h-[48px] border-b" />;
                    
                    return (
                      <div key={task.id} className="h-[48px] border-b flex items-center relative group">
                        <div 
                          className={`absolute h-6 rounded-md shadow-sm border flex items-center px-2 transition-all group-hover:ring-2 ring-primary ring-offset-1 ${
                            task.status === 'done' ? 'bg-green-100 border-green-200 text-green-800' :
                            task.status === 'in_progress' ? 'bg-blue-100 border-blue-200 text-blue-800' :
                            'bg-background text-foreground'
                          }`}
                          style={{
                            left: pos.left,
                            width: pos.width
                          }}
                        >
                          <span className="text-[10px] font-medium truncate">
                            {Math.round((pos.width / COLUMN_WIDTH) * 100) / 100}d
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernGanttChart;
